import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/FeedbackStyles.css";
import backArrow from "../assets/feedBackNavArrow.svg";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { LocalStorage } from "../../../utils/Utils";
import { useApi } from "../../../service/ApiContext";
import {
  FeedbackItem,
  renderFeedbackMessage,
} from "../../../components/FeedbackMessageRenderer";
import { useAlert } from "../../../components/AlertContextComponent";

interface FeedbackResponse {
  number_of_records: number;
  feedback: FeedbackItem[];
}

const FeedBackViewPage: React.FC = () => {
  const { showAlert } = useAlert();
  const [feedBack, setFeedBack] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const userData = useSelector((state: RootState) => state.profile.profileKeys);
  const api = useApi();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const get_feedback = async (page: number, isInitialLoad: boolean = false) => {
    if (loading) return;
    if (!hasMore && !isInitialLoad) return;

    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    setLoading(true);

    try {
      const res = await api.get<FeedbackResponse>("/feedback", {
        page_number: page,
        limit: 15,
      });

      const feedbacks = res.data.feedback || [];
      const total = res.data.number_of_records || 0;

      setTotalRecords(total);

      // Check if there are more records to fetch
      const currentTotal = isInitialLoad ? 0 : feedBack.length;
      const newTotal = currentTotal + feedbacks.length;
      setHasMore(newTotal < total);

      if (isInitialLoad) {
        // Reversing to show latest first
        setFeedBack(feedbacks.reverse());
      } else {
        // Append new feedbacks (already reversed from API)
        setFeedBack((prev) => [...prev, ...feedbacks.reverse()]);
      }

      setPageNumber(page);
    } catch (error) {
      showAlert({
        identity: "",
        show: true,
        title: "Error",
        message: "Something went wrong",
        alertType: "error",
        onlyNeedOneButton: true,
        button1Text: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    get_feedback(1, true);
  }, []);
  const handleResourceClick = (feedback: FeedbackItem) => {
    if (!feedback.video_id) return;

    navigate("/dashboard/learning-room/view-courses/video", {
      state: {
        program_id: feedback.program_id,
        course_id: feedback.course_id,
        topic_id: feedback.topic_id,
        video_id: feedback.video_id,
        program_category: feedback.program_category,
        labSession: feedback.is_lab_session,
        selectedProgramTitle: feedback.program_title,
      },
    });
  };
  // Scroll handler with throttling
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (loading || !hasMore) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Trigger when user scrolls to bottom (with 100px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      get_feedback(pageNumber + 1, false);
    }
  }, [loading, hasMore, pageNumber]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "??";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper for date ordinal suffix
  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString.replace(/,/, ""));
      if (isNaN(date.getTime())) return dateString;
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      const year = date.getFullYear();
      return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="v2-feedbackPage-container">
      <div className="v2-feedbackPage-header">
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => navigate(-1)}
        >
          <img
            src={backArrow}
            alt=""
            style={{ height: "25px", width: "25px", marginRight: "10px" }}
          />
          Feedback ({totalRecords})
        </div>
      </div>
      <div className="v2-feedbackPage-list-container">
        <div
          ref={scrollContainerRef}
          className="v2-dashboard-feedback-scroll"
          style={{
            width: "100%",
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            padding: "0 20px",
          }}
        >
          {feedBack.map((feedback, index) => {
            const senderName =
              feedback.institute_name ||
              feedback.instructor_name ||
              feedback.creator_name ||
              "Institute Dev";
            const senderRole = feedback.role_name || "Admin";

            return (
              <div
                key={feedback.feedback_id || index}
                className="v2-dashboard-feedback-item"
              >
                <div className="v2-dashboard-feedback-user-row">
                  <div className="v2-dashboard-feedback-avatar">
                    {getInitials(senderName)}
                  </div>

                  <div className="v2-dashboard-feedback-user-content">
                    <div className="v2-dashboard-feedback-user-header">
                      <span className="v2-dashboard-feedback-name">
                        {senderName}
                      </span>

                      <span className="v2-dashboard-feedback-role">
                        {senderRole}
                      </span>

                      <span className="v2-dashboard-feedback-date">
                        {formatDate(feedback.feedback_posted_time)}
                      </span>
                    </div>

                    <div className="v2-dashboard-feedback-message-wrapper">
                      {renderFeedbackMessage(
                        feedback.feedback_message,
                        feedback,
                        handleResourceClick,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Loading indicator at bottom
          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#666",
                fontSize: "14px",
              }}
            >
              Loading more feedback...
            </div>
          )} */}

          {/* No more records message
          {!hasMore && feedBack.length > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#999",
                fontSize: "13px",
              }}
            >
              No more feedback to load
            </div>
          )} */}

          {/* Empty state */}
          {feedBack.length === 0 && !loading && (
            <div
              className="v2-dashboard-feedback-empty-state"
              style={{ textAlign: "center", padding: "40px", color: "#666" }}
            >
              <p>No feedback available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedBackViewPage;
